import { Container, Graphics } from "pixi.js";

export function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

export function lerp(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

export function easeOutCubic(value: number): number {
  const inverse = 1 - clamp01(value);
  return 1 - inverse * inverse * inverse;
}

export function easeOutBack(value: number): number {
  const progress = clamp01(value);
  const overshoot = 1.70158;
  return 1 + (overshoot + 1) * Math.pow(progress - 1, 3) + overshoot * Math.pow(progress - 1, 2);
}

type ParticleKind = "dust" | "leaf" | "spark" | "stone" | "smoke";

type Particle = {
  display: Graphics;
  ageMs: number;
  durationMs: number;
  velocityX: number;
  velocityY: number;
  gravity: number;
  drag: number;
  spin: number;
  startScale: number;
  endScale: number;
  fadeIn: number;
};

export type BurstOptions = {
  kind: ParticleKind;
  count: number;
  color?: number;
  spreadX?: number;
  speedMin?: number;
  speedMax?: number;
  upwardBias?: number;
  durationMinMs?: number;
  durationMaxMs?: number;
};

function createParticleGraphic(kind: ParticleKind, color?: number): Graphics {
  const graphic = new Graphics();
  if (kind === "leaf") {
    graphic.ellipse(0, 0, 4.5, 1.8).fill({ color: color ?? 0x789348, alpha: 0.88 });
  } else if (kind === "spark") {
    graphic.moveTo(-4, 0).lineTo(4, 0).stroke({ color: color ?? 0xf4c65b, width: 2, alpha: 0.96 });
  } else if (kind === "stone") {
    graphic.roundRect(-2.5, -2, 5, 4, 1).fill({ color: color ?? 0x8b8578, alpha: 0.92 });
  } else if (kind === "smoke") {
    graphic.circle(0, 0, 6).fill({ color: color ?? 0xb9b3a2, alpha: 0.28 });
  } else {
    graphic.circle(0, 0, 3.5).fill({ color: color ?? 0xb99961, alpha: 0.48 });
  }
  return graphic;
}

export class ParticleSystem {
  private readonly layer: Container;
  private readonly particles: Particle[] = [];

  constructor(layer: Container) {
    this.layer = layer;
  }

  burst(x: number, y: number, options: BurstOptions): void {
    const spreadX = options.spreadX ?? Math.PI * 2;
    const speedMin = options.speedMin ?? 18;
    const speedMax = options.speedMax ?? 64;
    const upwardBias = options.upwardBias ?? 0;
    const durationMinMs = options.durationMinMs ?? 420;
    const durationMaxMs = options.durationMaxMs ?? 850;
    for (let index = 0; index < options.count; index += 1) {
      const display = createParticleGraphic(options.kind, options.color);
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * spreadX;
      const speed = speedMin + Math.random() * (speedMax - speedMin);
      const durationMs = durationMinMs + Math.random() * (durationMaxMs - durationMinMs);
      display.position.set(x + (Math.random() - 0.5) * 12, y + (Math.random() - 0.5) * 7);
      display.rotation = Math.random() * Math.PI * 2;
      this.layer.addChild(display);
      this.particles.push({
        display,
        ageMs: 0,
        durationMs,
        velocityX: Math.cos(angle) * speed + (Math.random() - 0.5) * upwardBias,
        velocityY: Math.sin(angle) * speed - upwardBias,
        gravity: options.kind === "smoke" ? -7 : options.kind === "leaf" ? 26 : 82,
        drag: options.kind === "smoke" ? 0.96 : options.kind === "leaf" ? 0.985 : 0.975,
        spin: (Math.random() - 0.5) * (options.kind === "leaf" ? 7 : 11),
        startScale: options.kind === "smoke" ? 0.55 : 0.75 + Math.random() * 0.45,
        endScale: options.kind === "smoke" ? 1.75 : options.kind === "dust" ? 1.35 : 0.45,
        fadeIn: options.kind === "smoke" ? 0.18 : 0,
      });
    }
  }

  tick(elapsedMs: number): void {
    const seconds = elapsedMs / 1_000;
    for (let index = this.particles.length - 1; index >= 0; index -= 1) {
      const particle = this.particles[index] as Particle;
      particle.ageMs += elapsedMs;
      const progress = clamp01(particle.ageMs / particle.durationMs);
      if (progress >= 1 || particle.display.destroyed) {
        if (!particle.display.destroyed) {
          particle.display.destroy();
        }
        this.particles.splice(index, 1);
        continue;
      }
      particle.velocityY += particle.gravity * seconds;
      const drag = Math.pow(particle.drag, elapsedMs / 16.667);
      particle.velocityX *= drag;
      particle.velocityY *= drag;
      particle.display.x += particle.velocityX * seconds;
      particle.display.y += particle.velocityY * seconds;
      particle.display.rotation += particle.spin * seconds;
      const fadeIn = particle.fadeIn > 0 ? Math.min(1, progress / particle.fadeIn) : 1;
      particle.display.alpha = fadeIn * Math.pow(1 - progress, 1.35);
      const scale = lerp(particle.startScale, particle.endScale, progress);
      particle.display.scale.set(scale);
    }
  }

  clear(): void {
    this.particles.splice(0).forEach(function (particle) {
      if (!particle.display.destroyed) {
        particle.display.destroy();
      }
    });
  }
}

type TimedEffect = {
  container: Container;
  ageMs: number;
  durationMs: number;
  update: (progress: number, ageMs: number) => void;
};

export class TimedEffectSystem {
  private readonly layer: Container;
  private readonly effects: TimedEffect[] = [];

  constructor(layer: Container) {
    this.layer = layer;
  }

  add(
    container: Container,
    durationMs: number,
    update: (progress: number, ageMs: number) => void,
    initialAgeMs = 0,
  ): void {
    const effect = { container, ageMs: Math.max(0, initialAgeMs), durationMs, update };
    this.layer.addChild(container);
    this.effects.push(effect);
    update(clamp01(effect.ageMs / durationMs), effect.ageMs);
  }

  tick(elapsedMs: number): void {
    for (let index = this.effects.length - 1; index >= 0; index -= 1) {
      const effect = this.effects[index] as TimedEffect;
      effect.ageMs += elapsedMs;
      const progress = clamp01(effect.ageMs / effect.durationMs);
      effect.update(progress, effect.ageMs);
      if (progress >= 1 || effect.container.destroyed) {
        if (!effect.container.destroyed) {
          effect.container.destroy({ children: true });
        }
        this.effects.splice(index, 1);
      }
    }
  }

  clear(): void {
    this.effects.splice(0).forEach(function (effect) {
      if (!effect.container.destroyed) {
        effect.container.destroy({ children: true });
      }
    });
  }
}
